// domain/model/ServiceDelivery.js

/**
 * ServiceDelivery — what is delivered, and where, for a single encounter.
 *
 * A VALUE OBJECT: no identity, no lifecycle, and no continuity across the copies a plan makes into
 * a session and then an activity. Two encounters holding the same place and the same service hold
 * the same value; nothing anywhere needs to tell one instance from another.
 *
 * NEUTRAL AS TO MOOD. The carrier supplies that: on a session plan it reads as intent, on a signed
 * activity as attested fact. The value itself asserts neither, which is why there is one type here
 * rather than an intended one and an attested one.
 *
 * IT HOLDS IDS INTO TWO CATALOGS, not the catalog records. Place of service and treatment service
 * are independent things a clinic configures separately; they are bundled here only because one
 * encounter co-locates them, the way a street and a city are bundled into an address.
 *
 * NOT A BILLING FACT. These are clinical inputs. The code, units and modifiers that reach a claim
 * are computed downstream at the date of service and are no business of this type.
 *
 * CALLERS DEAL IN TREATMENT SERVICE IDS; THIS TYPE OWNS THE ENTRY WRAPPER. The stored shape nests
 * each service in `{ treatmentServiceId }` so an entry can grow a second field later. Exposing that
 * would put the wrapper in every caller's hands and make growing it a change to all of them, so the
 * factory takes ids, the reader returns ids, and nothing outside constructs an entry.
 *
 * @domainObject value-object
 * @invariant Frozen — a delivery is replaced, never edited in place.
 * @invariant `serviceEntries` is always an array; absent and empty are the same thing.
 * @invariant A treatment service appears at most once.
 * @typedef {Object} ServiceDelivery
 * @property {string|null} placeOfServiceId - where the encounter happens; null = not chosen
 * @property {ReadonlyArray<{treatmentServiceId: string}>} serviceEntries - what is delivered
 */

/**
 * Builds a delivery from the ids a form holds.
 *
 * Duplicate ids collapse, because two entries naming one service say nothing a single entry does
 * not, and a multi-select can emit them.
 *
 * @param {Object} [attrs]
 * @param {string|null} [attrs.placeOfServiceId]
 * @param {string[]} [attrs.treatmentServiceIds]
 * @returns {ServiceDelivery}
 */
export function createServiceDelivery({ placeOfServiceId = null, treatmentServiceIds = [] } = {}) {
  const seen = new Set();
  const serviceEntries = (treatmentServiceIds ?? [])
    .filter((treatmentServiceId) => {
      if (!treatmentServiceId || seen.has(treatmentServiceId)) return false;
      seen.add(treatmentServiceId);
      return true;
    })
    .map((treatmentServiceId) => Object.freeze({ treatmentServiceId }));

  return Object.freeze({
    placeOfServiceId: placeOfServiceId ?? null,
    serviceEntries: Object.freeze(serviceEntries),
  });
}

/** A delivery with nothing chosen. Distinct from no delivery at all — see the mapper. */
export const EMPTY_SERVICE_DELIVERY = createServiceDelivery();

/**
 * @param {ServiceDelivery} delivery
 * @returns {string|null}
 */
export function placeOfServiceIdOf(delivery) {
  return delivery?.placeOfServiceId ?? null;
}

/**
 * @param {ServiceDelivery} delivery
 * @returns {string[]}
 */
export function treatmentServiceIdsOf(delivery) {
  return (delivery?.serviceEntries ?? []).map((entry) => entry.treatmentServiceId);
}

/**
 * True when nothing has been chosen. A delivery in this state is still a delivery — the plan editor
 * saves it, because refusing to save an unset value is what stops anyone filling the rest in.
 *
 * @param {ServiceDelivery} delivery
 * @returns {boolean}
 */
export function isEmptyServiceDelivery(delivery) {
  return placeOfServiceIdOf(delivery) === null && treatmentServiceIdsOf(delivery).length === 0;
}

/**
 * @param {ServiceDelivery} delivery
 * @param {string|null} placeOfServiceId
 * @returns {ServiceDelivery}
 */
export function withPlaceOfService(delivery, placeOfServiceId) {
  return createServiceDelivery({
    placeOfServiceId,
    treatmentServiceIds: treatmentServiceIdsOf(delivery),
  });
}

/**
 * @param {ServiceDelivery} delivery
 * @param {string[]} treatmentServiceIds
 * @returns {ServiceDelivery}
 */
export function withTreatmentServices(delivery, treatmentServiceIds) {
  return createServiceDelivery({
    placeOfServiceId: placeOfServiceIdOf(delivery),
    treatmentServiceIds,
  });
}

/**
 * Whether two deliveries say the same thing. Value objects compare by value, and the pair is
 * replaced as a whole rather than merged field by field — a merge produces a pair neither source
 * authored, which is the failure this type exists to make impossible.
 *
 * @param {ServiceDelivery} a
 * @param {ServiceDelivery} b
 * @returns {boolean}
 */
export function isSameServiceDelivery(a, b) {
  if (placeOfServiceIdOf(a) !== placeOfServiceIdOf(b)) return false;

  const left = treatmentServiceIdsOf(a);
  const right = treatmentServiceIdsOf(b);

  return left.length === right.length && left.every((id, index) => id === right[index]);
}
